import { skipToken } from "@reduxjs/toolkit/query";

import { Option } from "api/common/types/common.types";
import {
  useGetPcmFormLabsQuery,
  useGetPcmFormMaintenancesQuery,
  useGetPcmFormProductsQuery,
} from "api/public/pcm/pcm.api";
import { MaintenanceCC, ProductCC } from "api/public/pcm/pcm.types";

const CC_PRODUCTS = [] as ProductCC[];
const CC_LABS = [] as Option[];
const CC_MAINTENANCES = [] as MaintenanceCC[];

interface usePcmCascadingDataProps {
  pcmRid: number | undefined;
  schemeCode: string;
  isMaintenance: boolean;
  ccid: string;
}
/**
 * Hook that subscribes to form state and fetches cascading data reactively.
 * Must be called AFTER the form is created since it uses useStore with form.store.
 */
export const usePcmCascadingData = ({
  pcmRid,
  schemeCode,
  isMaintenance,
  ccid,
}: usePcmCascadingDataProps) => {
  // Subscribe to form values that drive cascading queries

  // Fetch products based on current scheme selection
  const { data: ccProducts = CC_PRODUCTS, isFetching: isFetchingCcProducts } =
    useGetPcmFormProductsQuery(
      schemeCode
        ? {
            scheme: schemeCode,
            is_maintenance: isMaintenance,
            rid: pcmRid,
          }
        : skipToken,
    );

  // Fetch labs based on current scheme selection
  const { data: ccLabs = CC_LABS, isFetching: isFetchingCcLabs } =
    useGetPcmFormLabsQuery(schemeCode ? { scheme: schemeCode } : skipToken);

  // Fetch maintenances based on current product selection (only when in maintenance mode)
  const {
    data: ccMaintenances = CC_MAINTENANCES,
    isFetching: isFetchingCcMaintenances,
  } = useGetPcmFormMaintenancesQuery(
    isMaintenance && ccid ? { ccid: ccid, rid: pcmRid } : skipToken,
  );

  return {
    ccProducts,
    ccLabs,
    ccMaintenances,
    isFetchingCascadingData:
      isFetchingCcProducts || isFetchingCcLabs || isFetchingCcMaintenances,
  };
};
